import React, { useCallback, useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import { HandsClapping } from '@phosphor-icons/react';

import { useReviewsSelection } from './context';
import { SuggestionsHeader } from './suggestions-header';
import type { ReviewListItemProps } from './review-list';

import { countSuggestions, countSuggestionsInElement } from '@/lib/countSuggestions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';

export type KnowledgeReviewPanelProps = {
  diffTitle: string;
  items: ReviewListItemProps[];
  isLoading?: boolean;
  totalItemsCount?: number;
  showActionButtons?: boolean;
  children?: React.ReactNode;
  sourceIcon: React.ReactNode;
  sourceDescription: string | null;
};

/**
 * Panel that shows the selected diff (title + content), suggestion count, and article nav.
 * Must be used inside ReviewsSelectionProvider. Expects --knowledge-review-scroll-height
 * to be set by a parent for ScrollArea height.
 */
export function KnowledgeReviewPanel({
  diffTitle,
  items,
  isLoading,
  totalItemsCount,
  showActionButtons = true,
  children,
  sourceIcon,
  sourceDescription,
}: KnowledgeReviewPanelProps) {
  const {
    selectedItem,
    setSelectedItem,
    loadedDiffDoc,
    loadingDiff,
    onApproveAllSuggestions,
    onRejectAllSuggestions,
    onAcceptAndPublish,
    onOpenArticleLink,
    onSourceLinkClick,
    breadcrumb,
    suggestionReasonBody,
  } = useReviewsSelection();

  const hasItems = items.length > 0;
  const resolvedIsLoading = isLoading ?? !hasItems;
  const isLoadingState = resolvedIsLoading && !hasItems;
  const isEmptyState = !resolvedIsLoading && !hasItems;

  const title = loadingDiff ? '' : parse(loadedDiffDoc?.diffTitle ?? diffTitle ?? '');
  const body = loadingDiff ? <div className="text-muted-foreground">Loading diff…</div> : loadedDiffDoc?.diffContent;

  const titleSuggestionsCount = loadingDiff ? 0 : countSuggestions(loadedDiffDoc?.diffTitle ?? diffTitle);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodySuggestionsCount, setBodySuggestionsCount] = useState(0);
  useEffect(() => {
    if (loadingDiff || !loadedDiffDoc) {
      setBodySuggestionsCount(0);

      return;
    }
    const el = bodyRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      setBodySuggestionsCount(countSuggestionsInElement(el));
    });

    return () => cancelAnimationFrame(id);
  }, [loadingDiff, loadedDiffDoc]);

  const suggestionsCount = titleSuggestionsCount + bodySuggestionsCount;

  const totalArticles = items.length;
  const totalArticlesForDisplay = totalItemsCount ?? totalArticles;
  const currentArticleIndex = selectedItem
    ? Math.max(
        0,
        items.findIndex((i) => i.id === selectedItem.id),
      )
    : 0;

  const onNextArticle = useCallback(() => {
    if (currentArticleIndex < totalArticles - 1) {
      setSelectedItem(items[currentArticleIndex + 1] ?? null);
    }
  }, [currentArticleIndex, items, setSelectedItem, totalArticles]);

  const onPreviousArticle = useCallback(() => {
    if (currentArticleIndex > 0) {
      setSelectedItem(items[currentArticleIndex - 1] ?? null);
    }
  }, [currentArticleIndex, items, setSelectedItem]);

  const headerSourceIcon = selectedItem?.icon ?? sourceIcon;
  const headerSourceDescription = selectedItem?.description ?? sourceDescription;

  return (
    <Card className="py-6 pl-4" data-name="knowledge-reviews">
      <ScrollArea className="h-[calc(var(--knowledge-review-scroll-height)-50px)] pr-8">
        <CardHeader className="p-0">
          {hasItems && (
            <CardTitle className="border-b border-dark-300 mb-6 pb-2 font-normal">
              <SuggestionsHeader
                title={title}
                isNew={loadedDiffDoc?.isNew ?? false}
                suggestionsCount={suggestionsCount}
                sourceIcon={headerSourceIcon}
                sourceDescription={headerSourceDescription}
                currentArticleIndex={currentArticleIndex}
                totalArticles={totalArticles}
                displayTotalArticles={totalArticlesForDisplay}
                showActionButtons={showActionButtons}
                onNextArticle={onNextArticle}
                onPreviousArticle={onPreviousArticle}
                onRejectAllSuggestions={onRejectAllSuggestions}
                onAcceptAllSuggestions={onApproveAllSuggestions}
                onAcceptAndPublish={onAcceptAndPublish}
                onOpenArticleLink={onOpenArticleLink}
                onSourceLinkClick={onSourceLinkClick}
                breadcrumb={breadcrumb}
                suggestionReasonBody={suggestionReasonBody}
              />
            </CardTitle>
          )}
        </CardHeader>
        <CardContent className="p-0 [&_ol]:!pl-16 [&_ul]:!pl-16">
          {/* Mount point for children (e.g. KnowledgeReviewDiffLoader). Kept in DOM so its effects run; hidden because it has no UI. */}
          <div aria-hidden="true" className="sr-only" hidden>
            {children}
          </div>
          {isLoadingState ? (
            <div className="flex flex-col items-center justify-center gap-4 text-center h-[50dvh] text-muted-foreground">
              <Spinner className="size-8" />
              <p className="text-base font-medium">Loading suggestions...</p>
            </div>
          ) : isEmptyState ? (
            <div className="flex flex-col items-center justify-center gap-4 text-center h-[50dvh]">
              <HandsClapping className="text-muted-foreground" size={32} weight="fill" />
              <p className="text-base font-medium text-foreground">All up to date. Well done, mate.</p>
            </div>
          ) : (
            <div ref={bodyRef}>{body}</div>
          )}
        </CardContent>
      </ScrollArea>
    </Card>
  );
}
